<template>
  <Modal
    :value="value"
    v-on="$listeners"
    :title="item.chocoPackage.Title"
    :subtitle="`${item.chocoPackage.Id} - ${nameAndVersion.version}`"
  >
    <template v-if="item" slot="body" class=" content">
      <div
        v-if="keepInSyncTextVisible || hasParam(item.command.parameters, '-n')"
        class="notification is-info paragraphs"
      >
        <p v-if="keepInSyncTextVisible">
          The command action was triggered by the computer agent to keep in
          sync.
        </p>

        <p
          v-if="
            hasParam(item.command.parameters, '-n') &&
              item.command.status == 'done'
          "
        >
          <span v-if="item.command.name == 'install'"
            >The package was only added to the computer, not installed.</span
          >
          <span v-else-if="item.command.name == 'uninstall'"
            >The package was only removed from the computer, not
            uninstalled.</span
          >
        </p>
      </div>

      <div class="box" id="package-info">
        <table class="table is-hoverable is-fullwidth">
          <tr>
            <td>
              Command
            </td>
            <td>
              <span class="icon has-text-primary">
                <i class="mdi mdi-cog"></i>
              </span>
              <span class="is-capitalized">
                {{ item.command.name }}
              </span>
            </td>
          </tr>

          <tr>
            <td>
              Parameters
            </td>
            <td>
              <span class="icon has-text-primary">
                <i class="mdi mdi-cogs"></i>
              </span>
              <span v-if="item.command.parameters">
                {{ item.command.parameters.split("|").join(" ") }}
              </span>
              <span v-else>
                None
              </span>
            </td>
          </tr>

          <tr v-if="item.command.execDone">
            <td>
              When
            </td>
            <td>
              <span class="icon has-text-primary">
                <i class="mdi mdi-clock-outline"></i>
              </span>
              <span>
                {{ fromNow(item.command.execDone) }}
              </span>
            </td>
          </tr>

          <tr v-if="item.command.execStart">
            <td>
              <span> Started </span>
            </td>
            <td>
              <span class="icon has-text-primary">
                <i class="mdi mdi-ray-start"></i>
              </span>
              <span>
                {{ toIso(item.command.execStart) }}
              </span>
            </td>
          </tr>

          <tr v-if="item.command.execDone">
            <td>
              Ended
            </td>
            <td>
              <span class="icon has-text-primary">
                <i class="mdi mdi-ray-end"></i>
              </span>
              <span>
                {{ toIso(item.command.execDone) }}
              </span>
            </td>
          </tr>

          <tr v-if="item.command.execStart && item.command.execDone">
            <td>
              Duration
            </td>
            <td>
              <span class="icon has-text-primary">
                <i class="mdi mdi-ray-start-end"></i>
              </span>
              <span>
                {{ duration(item.command.execStart, item.command.execDone) }}
                min
              </span>
            </td>
          </tr>

          <tr v-if="item.command.execUser">
            <td>
              Runned as
            </td>
            <td>
              <span class="icon has-text-primary">
                <i class="mdi mdi-account-circle"></i>
              </span>
              <span>
                {{ item.command.execUser }}
              </span>
            </td>
          </tr>

          <tr>
            <td>
              Status
            </td>
            <td>
              <span class="icon">
                <i
                  class="mdi "
                  :class="{
                    'mdi-check has-text-primary':
                      item.command.status === 'done',
                    'mdi-alert-circle-outline has-text-danger':
                      item.command.status === 'failed',
                    'mdi-timer-sand-empty has-text-info':
                      item.command.status === 'working',
                    'mdi-clock-start has-text-info':
                      item.command.status === 'pending',
                  }"
                ></i>
              </span>
              <span class="is-capitalized">
                {{ item.command.status }}
              </span>
            </td>
          </tr>
          <tr
            v-if="item.command.status === 'working' && item.command.streamLog"
          >
            <td>Log</td>
            <td>
              <span class="icon has-text-info pulse">
                <i class="mdi mdi-progress-clock"></i>
              </span>
              <span>Streaming</span>
            </td>
          </tr>
        </table>
      </div>

      <p v-if="loading" class="text-info">
        Fetching log
        <span class="margin-left-5 spinner small"></span>
      </p>

      <pre
        v-if="
          (item.command.execLog && item.command.execLog !== '') ||
            (item.command.status === 'working' && item.command.streamLog)
        "
      >
        {{ item.command.execLog }}        
        </pre
      >
      <p
        v-if="
          !loading && (!item.command.execLog || item.command.execLog === '')
        "
        class="text-info"
      >
        <i v-if="!item.command.streamLog">No log availiable.</i>
      </p>
    </template>
    <template slot="footer" class="content">
      <div
        v-if="item.command.status !== 'working'"
        class="button is-primary"
        @click="
          $emit('input', false);
          $emit('openActions', item);
        "
      >
        Open actions
      </div>
      <div class="button" @click="$emit('input', false)">Close</div>
    </template>
  </Modal>
</template>

<style lang="css" scoped>
.up-right {
  position: absolute;
  top: 30px;
  right: 60px;
}
</style>

<script>
import { getPackageNameVersionAndHash, toIsoDate, hasParam } from "@/utils/mdc";
import { postApi } from "@/utils/http";
import { fromNow, duration } from "@/utils/date";

export default {
  components: { Modal: () => import("./Modal") },
  props: ["value", "itemRef"],
  data() {
    return {
      show: false,
      loading: false,
    };
  },

  computed: {
    item() {
      const existing = this.$store.getters["computerPackage/mergedPackage"](
        this.itemRef
      );

      if (!existing) {
        return null;
      }

      return existing;
    },

    nameAndVersion() {
      return getPackageNameVersionAndHash(this.item);
    },

    duration() {
      return (date1, date2) => {
        return duration(date1, date2);
      };
    },

    fromNow() {
      return (date) => {
        return fromNow(date);
      };
    },

    toIso() {
      return (date) => {
        return toIsoDate(date);
      };
    },

    keepInSyncTextVisible() {
      if (!this.item.command.execDone) {
        return false;
      }

      const end = new Date(this.item.command.execDone);
      const enabledFeature = end.getFullYear() >= 2019;
      if (!enabledFeature) {
        return;
      }

      return !this.item.command.execUser && this.item.command.execDone;
    },
  },
  async mounted() {
    this.loading = true;

    if (this.item) {
      if (
        this.item &&
        this.item.computerUID &&
        !this.item.command.execLog &&
        !this.item.streamLog
      ) {
        try {
          const actionLogResult = await postApi("get/computerPackageLog", {
            packageHash: this.item.version.packageHash,
            computerUID: this.item.computerUID,
            domainUID: this.$route.params.domainUID,
          });
          if (actionLogResult[0]) {
            this.$store.commit("computerPackage/editMergedPackage", {
              p: this.item,
              obj: {
                command: { ...this.item.command, execLog: actionLogResult[0] },
              },
            });
          }
        } catch (e) {
          this.$helpers.notify(
            "Error",
            "Could not get log data.",
            "danger",
            5000
          );
        }
      }
    }

    this.loading = false;
  },
  methods: {
    hasParam: hasParam,
  },
};
</script>

<style scoped>
#package-info {
  margin-bottom: 25px;
}

.text-info {
  text-align: center;
}
</style>
